import { useQuery } from "@apollo/client";
import { BrandQuery } from "../../gql/__generated__/graphql";
import { GET_BRAND } from "../apis/lambda/GraphQL/Brand/queries";
import { GET_ALL_PRODUCTS_FOR_BRAND } from "../apis/lambda/GraphQL/Product/queries";
import { GET_USER } from "../apis/lambda/GraphQL/User/queries";
import { decodeJWTFromCookie } from "../utils/jwtUtils";

export interface SignupCompletionStatus {
  isEmailVerified: boolean;
  isProfileComplete: boolean;
  isBrandApproved: boolean; // Only relevant for BRAND_USER
  isThirdStepComplete: boolean;
  allStepsComplete: boolean;
  userType: "CREATOR" | "BRAND_USER" | null;
  user: any;
  brand: any;
  products: any[];
  loading: boolean;
}

export function useSignupCompletion(): SignupCompletionStatus {
  const userInfo = decodeJWTFromCookie();
  const email = userInfo?.email ?? "";
  const userType = userInfo?.userType;

  // Fetch user data
  const { data: userData, loading: userLoading } = useQuery(GET_USER, {
    variables: { email },
    fetchPolicy: "network-only",
    skip: !email,
  });

  const user = userData?.user;
  const brandId = user?.brandId;

  // Fetch brand data if user is a BRAND_USER
  const { data: brandData, loading: brandLoading } = useQuery<BrandQuery>(
    GET_BRAND,
    {
      variables: { id: brandId ?? "" },
      fetchPolicy: "network-only",
      skip: userType !== "BRAND_USER" || !brandId,
    }
  );

  // Fetch products if user is a BRAND_USER
  const { data: productsData, loading: productsLoading } = useQuery(
    GET_ALL_PRODUCTS_FOR_BRAND,
    {
      variables: { brandId: brandId ?? "" },
      fetchPolicy: "network-only",
      skip: userType !== "BRAND_USER" || !brandId,
    }
  );

  const brand = brandData?.brand;
  const products = productsData?.getAllProductsForBrand ?? [];

  // Determine completion status based on user type
  const isEmailVerified = user?.validated === true;

  let isProfileComplete = false;
  let isBrandApproved = true; // Default true for creators (not applicable)
  let isThirdStepComplete = false;

  if (userType === "CREATOR") {
    // For creators: check name and description
    isProfileComplete = !!(user?.name && user?.description);
    // For creators: third step is TikTok linking
    isThirdStepComplete = !!user?.tiktokData;
  } else if (userType === "BRAND_USER") {
    // For brands: check brand name and description
    isProfileComplete = !!(brand?.name && brand?.description);
    // For brands: check if user is approved AND brand is active
    isBrandApproved = user?.approved === true && brand?.status === "ACTIVE";
    // For brands: fourth step is having at least one product
    isThirdStepComplete = products.length > 0;
  }

  const allStepsComplete =
    isEmailVerified &&
    isProfileComplete &&
    isBrandApproved &&
    isThirdStepComplete;

  const loading = userLoading || brandLoading || productsLoading;

  return {
    isEmailVerified,
    isProfileComplete,
    isBrandApproved,
    isThirdStepComplete,
    allStepsComplete,
    userType: userType as "CREATOR" | "BRAND_USER" | null,
    user,
    brand,
    products,
    loading,
  };
}
